import { useAuth } from '@/features/auth/store'
import type { Action } from '@/features/auth/permissions'

/**
 * What a screen or a nav item needs before it is worth showing.
 *
 * A resource on its own is not enough to decide with. `pos` is granted as View to a sales
 * manager and as Full to a cashier, but the point-of-sale screen is a till: everything on
 * it -- opening the drawer, scanning, taking money -- needs `pos:create`. Gating it on
 * "holds something on pos" put a link in the sidebar that could only ever answer "You do
 * not have permission to create pos", which is the API telling the user something the UI
 * should have known before it drew the link.
 *
 * So a requirement names the action the destination actually performs, and the nav item
 * and the route are given the same one -- see `ROUTE_ACCESS` -- so a link can never lead
 * somewhere the guard will bounce.
 */
export interface Requirement {
  resource: string
  action: Action
}

/** The permission each guarded path needs. Paths absent from here are open to any signed-in user. */
export const ROUTE_ACCESS: Record<string, Requirement> = {
  '/analytics/sales': { resource: 'sales_analytics', action: 'read' },
  '/analytics/products': { resource: 'sales_analytics', action: 'read' },
  '/analytics/customers': { resource: 'sales_analytics', action: 'read' },
  '/analytics/inventory': { resource: 'sales_analytics', action: 'read' },
  '/orders': { resource: 'invoices', action: 'read' },
  '/orders/new': { resource: 'invoices', action: 'create' },
  '/orders/:orderId': { resource: 'invoices', action: 'read' },
  '/pos': { resource: 'pos', action: 'create' },
  '/pos/shift': { resource: 'pos', action: 'create' },
  '/pos/shifts': { resource: 'pos', action: 'read' },
  '/pos/terminals': { resource: 'pos', action: 'read' },
  '/pos/sales/:transactionId': { resource: 'pos', action: 'read' },
  '/products': { resource: 'products', action: 'read' },
  '/products/new': { resource: 'products', action: 'create' },
  '/products/:productId': { resource: 'products', action: 'read' },
  '/products/:productId/edit': { resource: 'products', action: 'update' },
  // Catalog structure, same as products/tax rules -- see `catalog.router`'s module docstring.
  '/categories': { resource: 'products', action: 'read' },
  '/stock/adjustments': { resource: 'inventory', action: 'read' },
  '/stock/valuation': { resource: 'inventory', action: 'read' },
  // Catalog structure, same as products/categories -- see `catalog.router`'s module docstring.
  '/tax-rules': { resource: 'products', action: 'read' },
  '/stock/locations': { resource: 'inventory', action: 'read' },
  '/invoices': { resource: 'invoices', action: 'read' },
  '/invoices/:invoiceId': { resource: 'invoices', action: 'read' },
  '/accounting/accounts': { resource: 'chart_of_accounts', action: 'read' },
  '/accounting/ledger/:accountId': { resource: 'chart_of_accounts', action: 'read' },
  '/accounting/journal': { resource: 'journal_entries', action: 'read' },
  '/accounting/journal/new': { resource: 'journal_entries', action: 'create' },
  '/accounting/journal/:entryId': { resource: 'journal_entries', action: 'read' },
  '/accounting/trial-balance': { resource: 'journal_entries', action: 'read' },
  // Read, not update, for both: a rep holds `commissions:read` and the statement is scoped
  // server-side to what they may see, so their own month is theirs to open. The payroll
  // preview writes nothing either -- the restate button inside it asks for `update`
  // separately, so a rep sees the report and not the lever.
  '/commissions/statement': { resource: 'commissions', action: 'read' },
  '/commissions/payroll': { resource: 'commissions', action: 'read' },
  // Read here too, same reasoning: the rate cards are visible to anyone who can see the
  // Commission Engine at all, and the New/Edit/Delete controls each ask for their own
  // action inside the screen so a rep sees the cards and not the levers.
  '/commissions/rules': { resource: 'commissions', action: 'read' },
  '/customers': { resource: 'customers', action: 'read' },
  '/customers/:customerId': { resource: 'customers', action: 'read' },
  // Create, not read: a lead is only worth finding for someone who may sign it up.
  '/leads': { resource: 'customers', action: 'create' },
  '/field-routes': { resource: 'field_operations', action: 'read' },
  '/employees': { resource: 'performance_reviews', action: 'read' },
  '/hr/reviews': { resource: 'performance_reviews', action: 'read' },
  '/hr/reviews/:reviewId': { resource: 'performance_reviews', action: 'read' },
  '/recruitment/postings': { resource: 'performance_reviews', action: 'read' },
  '/recruitment/candidates': { resource: 'performance_reviews', action: 'read' },
  '/recruitment/applications': { resource: 'performance_reviews', action: 'read' },
  '/recruitment/applications/:applicationId': { resource: 'performance_reviews', action: 'read' },
  '/hr/attendance': { resource: 'leave', action: 'read' },
  '/users': { resource: 'users', action: 'read' },
  '/roles': { resource: 'roles', action: 'read' },
  '/settings/company': { resource: 'system_settings', action: 'read' },
  // The health page. `/health/status` on the API is deliberately open -- a probe cannot
  // sign in -- so this gate is presentation only, but the screen is an operator's tool
  // sitting under Administration and a sales manager has no reading of it.
  '/status': { resource: 'system_settings', action: 'read' },
}

/** True when the signed-in user holds the requirement. An absent requirement is always true. */
export function useMay(requirement?: Requirement): boolean {
  const can = useAuth((s) => s.can)
  if (!requirement) return true
  return can(requirement.resource, requirement.action)
}

/**
 * True when this user may open `path`, which must be a key of `ROUTE_ACCESS`.
 *
 * For links that leave the resource they sit on -- stock valuation pointing at the trial
 * balance, a valuation line pointing at the product record. A cashier can read inventory
 * and not the catalog, an inventory manager can read stock and not the ledger, so both of
 * those links land some real role on a redirect unless the screen asks first.
 */
export function useMayOpen(path: keyof typeof ROUTE_ACCESS): boolean {
  return useMay(ROUTE_ACCESS[path])
}
